const MAPLIBRE_ROOT = "/static/vendor/maplibre-gl-5.24.0";
const ADAPTER_URL = "/static/vendor/maplibre-gl-leaflet-0.1.3/leaflet-maplibre-gl.js";
const BASEMAP_STYLE = "/static/maps/dispatch-v1.json";
const OSM_ATTRIBUTION = '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap</a>';

let libraryPromise;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => {
      script.remove();
      reject(new Error(`Impossible de charger ${src}`));
    };
    document.head.appendChild(script);
  });
}

function loadVectorLibrary() {
  if (!libraryPromise) {
    libraryPromise = (async () => {
      if (!document.querySelector('link[data-route-map-vector-css]')) {
        const css = document.createElement("link");
        css.rel = "stylesheet";
        css.href = `${MAPLIBRE_ROOT}/maplibre-gl.css`;
        css.dataset.routeMapVectorCss = "true";
        document.head.appendChild(css);
      }
      if (!window.maplibregl) await loadScript(`${MAPLIBRE_ROOT}/maplibre-gl.js`);
      if (!window.L.maplibreGL) await loadScript(ADAPTER_URL);
    })().catch((error) => {
      libraryPromise = null;
      throw error;
    });
  }
  return libraryPromise;
}

// The existing Leaflet overlays, events and view remain owned by useRouteMap.
// Keep OSM visible until the vector map has actually rendered its first view.
export function attachRouteMapBasemap(map, { L = window.L, loadLibrary = loadVectorLibrary } = {}) {
  const container = map.getContainer();
  map.attributionControl?.remove();
  const fallback = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: OSM_ATTRIBUTION,
    maxZoom: 19,
  }).addTo(map);
  container.dataset.routeMapBasemap = "raster";
  let disposed = false;
  let vectorLayer = null;
  let glMap = null;
  let timeout;

  const restoreRaster = () => {
    if (disposed) return;
    clearTimeout(timeout);
    if (!map.hasLayer(fallback)) fallback.addTo(map);
    if (vectorLayer) {
      glMap?.off("error", restoreRaster);
      glMap?.off("load", showVector);
      // The adapter can be registered in Leaflet before its WebGL constructor
      // throws. Its normal onRemove assumes that constructor succeeded.
      if (!vectorLayer.getMaplibreMap()) {
        const failedContainer = vectorLayer.getContainer();
        vectorLayer.onRemove = () => failedContainer?.remove();
      }
      vectorLayer.remove();
      vectorLayer = null;
    }
    container.dataset.routeMapBasemap = "raster";
  };

  const showVector = () => {
    if (disposed || !vectorLayer) return;
    clearTimeout(timeout);
    vectorLayer.getContainer().style.opacity = "1";
    fallback.remove();
    container.dataset.routeMapBasemap = "vector";
  };

  loadLibrary().then(() => {
    if (disposed) return;
    vectorLayer = L.maplibreGL({
      style: BASEMAP_STYLE,
      attributionControl: false,
      interactive: false,
      renderWorldCopies: true,
      // Dispatch captures export this canvas through html-to-image.
      canvasContextAttributes: { preserveDrawingBuffer: true },
    });
    vectorLayer.addTo(map);
    vectorLayer.getContainer().style.opacity = "0";
    glMap = vectorLayer.getMaplibreMap();
    glMap.on("load", showVector);
    glMap.on("error", restoreRaster);
    timeout = setTimeout(restoreRaster, 15000);
  }).catch(restoreRaster);

  return () => {
    disposed = true;
    clearTimeout(timeout);
    glMap?.off("error", restoreRaster);
    glMap?.off("load", showVector);
    // Leaflet's map.remove() disposes both layers and the WebGL context.
  };
}
